import React from 'react';

// material-ui
import { Box, Fade, IconButton } from '@mui/material';
import { useTheme } from '@mui/styles';

// assets
import { IconSquareMinus, IconSquarePlus } from '@tabler/icons';
import {NoNegativeInput, WithNegative} from './textFieldNumber';

interface HoverableIncrementerProps {
    /**
     * Initial counter value
     */
    value?: number;
    /**
     * Whether the value is static and cannot be changed
     */
    disabled?: boolean;
    cantChange?: boolean;
    /**
     * On decrement handler
     */
    onDecrement?: () => void;
    /**
     * On increment handler
     */
    onIncrement?: () => void;
    /**
     * On hover handler
     */
    onChange?: (v:number) => void;
    /**
     * On hover handler
     */
    onMouseEnter?: () => void;
    /**
     * On hover ends handler
     */
    onMouseLeave?: () => void;
    withNegative?:boolean
    /**
     * Wider input for amounts/prices so values like 1000.233 stay fully visible
     */
    wide?: boolean;
}

const CounterItem = ({ counter,setCounter,cantChange,withNegative,wide }: { counter: number,setCounter?:(number)=>void,cantChange?:boolean,withNegative?:boolean,wide?:boolean }) => {
    // Auto-grow with the value so long numbers like 10021.23 stay fully visible
    const widthCh = Math.max(wide ? 6 : 4, String(counter ?? '').length + 1.5);
    const inputSx = {
        textAlign: 'center' as const,
        borderRadius: 1,
        flex: '0 0 auto',
        width: `${widthCh}ch`,
        '& input': {
            textAlign: 'center',
            width: '100%',
            px: 0.5,
        },
    };
    return withNegative? <WithNegative
            id={'negativeinp'}
            changer={(v)=> setCounter?.(v)}
            type={'text'}
            value={counter}
            disabled={cantChange}
            sx={inputSx}
        />:
    <NoNegativeInput
        id={'negativeinp'}
        changer={(v)=> setCounter?.(v)}
        type={'text'}
        value={counter}
        disabled={cantChange}
        sx={inputSx}
    />;
};

const HoverableIncrementer = ({
    value,
    disabled = false,
    onDecrement,
    onIncrement,
    onMouseEnter,
    onMouseLeave,
    onChange,
    cantChange,
                                  withNegative,
                                  wide
}: HoverableIncrementerProps) => {
    const theme = useTheme();
    const [hover, setHover] = React.useState(false);
    const [counter, setCounter] = React.useState(value ?? 0);

    const handleOnMouseEnter = () => {
        onMouseEnter ? onMouseEnter() : setHover(true);
    };

    const handleOnMouseLeave = () => {
        onMouseLeave ? onMouseLeave() : setHover(false);
    };

    const handleOnDecrement = () => {
        onDecrement ? onDecrement() : setCounter((p) => p - 1);
    };

    const handleOnIncrement = () => {
        onIncrement ? onIncrement() : setCounter((p) => p + 1);
    };

    return (
        <Box

            sx={{ display: 'inline-flex', flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
        >
            <Fade in={!disabled && hover}>
                <IconButton size="small" sx={{ p: 0.25 }} onClick={handleOnDecrement}>
                    <IconSquareMinus size={18} color={theme.palette.primary.dark} />
                </IconButton>
            </Fade>
            <CounterItem counter={value ?? counter} setCounter={onChange} cantChange={cantChange} withNegative={withNegative} wide={wide}/>
            <Fade in={!disabled && hover}>
                <IconButton size="small" sx={{ p: 0.25 }} onClick={handleOnIncrement}>
                    <IconSquarePlus size={18} color={theme.palette.primary.dark} />
                </IconButton>
            </Fade>
        </Box>
    );
};

export default HoverableIncrementer;
